// Pure loop math: elapsed cycles → position along in→out → value. No clock, no state.
import { sampleRamp } from '~/lib/fillInterpolation'
import { hexToRgb01 } from '~/lib/shaders/common'

const clamp01 = v => Math.min(1, Math.max(0, v))
const mod = (v, n) => ((v % n) + n) % n

export const EASE = {
	linear: u => u,
	hold: () => 0,
	easeIn: u => u * u,
	easeOut: u => 1 - (1 - u) * (1 - u),
	easeInOut: u => (u < 0.5 ? 2 * u * u : 1 - 2 * (1 - u) * (1 - u)),
}

/** y at `f` on a conformed key list; each key's curve shapes the segment that starts at it. */
export const sampleEnvelope = (keys, f) => {
	let i = 0
	while (i < keys.length - 2 && f > keys[i + 1].x) i++
	const a = keys[i]
	const b = keys[i + 1]
	const span = b.x - a.x
	const u = span > 0 ? clamp01((f - a.x) / span) : 1
	return a.y + (b.y - a.y) * (EASE[a.curve] ?? EASE.linear)(u)
}

/** Passes in one cycle of the mode: a yoyo's cycle is there and back. */
export const periodOf = def => (def.mode === 'pingpong' || def.mode === 'pingpong_loop' ? 2 : 1)

/** `phase` (a fraction of the cycle) in cycles: where the playhead sits at the clock's zero. Phase 1 parks at the cycle's far end. */
export const phaseOffset = def => Math.min((def.phase ?? 0) * periodOf(def), periodOf(def) - PASS_END)

/** The phase that puts the playhead at position `p` on the first pass. */
export const phaseAtPosition = (def, p) => cyclesAtPosition(def, Math.min(1, Math.max(0, p))) / periodOf(def)

// A wrapping pass's far end is the next pass's beginning; a playhead parked there sits this far short of it.
const PASS_END = 1e-9

/** Passes a one-shot mode plays before it holds: `once` stops at out, `pingpong` (yoyo once) back at in. */
export const ONE_SHOT_CYCLES = { once: 1, pingpong: 2 }

const bounce = k => (k <= 1 ? k : 2 - k)

/** Position 0…1 along in→out after `cycles` passes; a one-shot holds where it ends. */
export const positionAt = (def, cycles) => {
	let f
	if (def.mode === 'once') f = clamp01(cycles)
	else if (def.mode === 'pingpong') f = bounce(Math.min(2, Math.max(0, cycles)))
	else if (def.mode === 'pingpong_loop') f = bounce(mod(cycles, 2))
	else f = mod(cycles, 1)
	return def.direction === 'reverse' ? 1 - f : f
}

/** `positionAt`'s inverse: the cycles that put the playhead at `p`, on the same pass as `near`. */
export const cyclesAtPosition = (def, p, near = 0) => {
	const f = def.direction === 'reverse' ? 1 - p : p
	if (def.mode === 'once') return f
	if (def.mode === 'pingpong') return near > 1 ? 2 - f : f
	if (def.mode === 'pingpong_loop') {
		const base = Math.floor(near / 2) * 2
		return near - base > 1 ? base + 2 - f : base + f
	}
	return Math.floor(near) + Math.min(f, 1 - PASS_END)
}

/** The param's value at position `f`: a number along in→out, or a colour's ramp at the shaped position. */
export const valueAtPosition = (def, f) => {
	const y = def.in + (def.out - def.in) * (def.envelope ? sampleEnvelope(def.envelope.keys, f) : f)
	return def.stops ? sampleRamp(def.stops, y) : y
}

// Steps an envelope or a ramp is searched in for the position giving a value.
const INVERSE_STEPS = 512

const distance = (a, b) => {
	if (typeof a === 'number') return Math.abs(a - b)
	const [p, q] = [hexToRgb01(a), hexToRgb01(b)]
	return Math.hypot(p[0] - q[0], p[1] - q[1], p[2] - q[2])
}

/** The first position whose value is nearest `v`: through the envelope or ramp when there is one, else straight along in→out. */
export const positionOfValue = (def, v) => {
	const span = def.out - def.in
	if (!(span > 0)) return 0
	if (!def.envelope && !def.stops) return clamp01((v - def.in) / span)
	let best = 0
	let gap = Infinity
	for (let i = 0; i <= INVERSE_STEPS; i++) {
		const d = distance(valueAtPosition(def, i / INVERSE_STEPS), v)
		if (d < gap) [best, gap] = [i / INVERSE_STEPS, d]
	}
	return best
}

/** Beat loops that repeat are locked to the bar grid; a seconds loop or a one-shot runs from its own origin. */
export const isPhaseLocked = def => def.drive === 'bpm' && !ONE_SHOT_CYCLES[def.mode]

/** The def's clock reading: beats or transport seconds. */
export const clockReading = (def, clock, t) => (def.drive === 'bpm' ? clock.beatAt(t) : clock.secondsAt(t))
